import { useEffect, useState } from "react";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { trackEvent } from "@/lib/track";
import Navbar, { scrollToHash, WHATSAPP_SUNIL } from "@/components/Navbar";
import Hero from "@/components/Hero";
import PriceStrip from "@/components/PriceStrip";
import Marquee from "@/components/Marquee";
import Journey from "@/components/Journey";
import PrimaryResale from "@/components/PrimaryResale";
import WhyEon from "@/components/WhyEon";
import Opportunities from "@/components/Opportunities";
import CompareProjects from "@/components/CompareProjects";
import Calculator from "@/components/Calculator";
import Location from "@/components/Location";
import WhyHomewiz from "@/components/WhyHomewiz";
import Enquiry from "@/components/Enquiry";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";

export default function Home() {
  const [channel, setChannel] = useState("all");

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    const trackClicks = (e) => {
      const a = e.target.closest("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      const location = a.getAttribute("data-testid") || "unknown";
      if (href.includes("wa.me")) trackEvent("whatsapp_click", { location });
      else if (href.startsWith("mailto:")) trackEvent("email_click", { location });
      else if (href.startsWith("tel:")) trackEvent("call_click", { location });
    };
    document.addEventListener("click", trackClicks);
    return () => document.removeEventListener("click", trackClicks);
  }, []);

  useEffect(() => {
    const fired = new Set();
    const trackScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      const pct = Math.round((window.scrollY / max) * 100);
      for (const threshold of [50, 90]) {
        if (pct >= threshold && !fired.has(threshold)) {
          fired.add(threshold);
          trackEvent("scroll_depth", { percent: threshold });
        }
      }
    };
    window.addEventListener("scroll", trackScroll, { passive: true });
    return () => window.removeEventListener("scroll", trackScroll);
  }, []);

  const selectChannel = (c) => {
    setChannel(c);
    scrollToHash("#opportunities");
  };

  return (
    <div id="top" className="grain bg-[#0B0C0E] text-[#F3F4F6] font-body antialiased overflow-x-clip">
      <Navbar />
      <main>
        <Hero />
        <PriceStrip />
        <Marquee />
        <Journey />
        <PrimaryResale onSelect={selectChannel} />
        <WhyEon />
        <Opportunities channel={channel} setChannel={setChannel} />
        <CompareProjects />
        <Calculator />
        <Location />
        <WhyHomewiz />
        <Enquiry />
      </main>
      <Footer />
      <CookieConsent />
      <motion.a
        href={WHATSAPP_SUNIL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp-btn"
        aria-label="WhatsApp Ashish"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#D4AF37] text-[#0B0C0E] flex items-center justify-center shadow-[0_8px_32px_rgba(212,175,55,0.45)] hover:scale-110 hover:bg-[#F3E5AB] transition-all duration-300"
      >
        <MessageCircle size={22} />
      </motion.a>
    </div>
  );
}
